import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, ShieldCheck, CheckCircle2, Ship, Anchor, 
  Calendar, MapPin, Users, Share2, Download, Copy, ExternalLink, Mail
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificatePage() {
  const navigate = useNavigate();
  const { checklistId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchCertificate();
  }, [checklistId]);

  const fetchCertificate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/${checklistId}`, { withCredentials: true });
      setCertificate(response.data);
    } catch (err) {
      console.error('Failed to load certificate:', err);
      toast.error('Failed to load certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const response = await axios.get(`${API}/certificates/${checklistId}/share`, { withCredentials: true });
      setShareUrl(response.data.share_url);
      
      // Try native share if available
      if (navigator.share) {
        await navigator.share({
          title: 'Trusted Boater Safety Certificate',
          text: `View my boating safety compliance certificate - ${certificate?.compliance?.score}% compliant`,
          url: response.data.share_url
        });
      } else {
        // Copy to clipboard
        await navigator.clipboard.writeText(response.data.share_url);
        toast.success('Share link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Failed to share:', err);
        toast.error('Failed to generate share link');
      }
    } finally {
      setSharing(false);
    }
  };

  const handleEmailCertificate = async () => {
    // Generate share URL first if not already generated
    let url = shareUrl;
    if (!url) {
      try {
        const response = await axios.get(`${API}/certificates/${checklistId}/share`, { withCredentials: true });
        url = response.data.share_url;
        setShareUrl(url);
      } catch (err) {
        toast.error('Failed to generate share link');
        return;
      }
    }

    const subject = encodeURIComponent(`Trusted Boater Safety Certificate - ${certificate?.vessel?.name}`);
    const body = encodeURIComponent(
`View my boating safety compliance certificate:

Vessel: ${certificate?.vessel?.name}
Class: ${certificate?.vessel?.class}
Compliance Score: ${certificate?.compliance?.score}%
Status: ${certificate?.compliance?.is_compliant ? 'COMPLIANT' : 'NOT COMPLIANT'}
${certificate?.compliance?.is_verified ? '✓ Verified Pre-Departure Safety Check' : ''}

View Certificate: ${url}

Certificate ID: ${certificate?.certificate_id}
Verification Code: ${certificate?.verification_code}

This certificate is valid for 24 hours from generation.

--
Generated by Trusted Boater
Florida FWC Compliance Checklist`
    );

    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopyLink = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleDownloadPDF = () => {
    window.print();
  };

  const verificationUrl = certificate
    ? `${window.location.origin}/certificate/view/${checklistId}`
    : '';

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2B3A67] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-[#D32F2F] font-bold">Certificate not found</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] pb-32 page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            data-testid="certificate-back-btn"
            onClick={() => navigate(-1)}
            className="text-[#2B3A67]"
          >
            <ArrowLeft className="w-6 h-6" />
          </Button>
          <h1 className="text-xl font-bold text-[#2B3A67] font-['Outfit']">Safety Certificate</h1>
          <div className="w-10"></div>
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto space-y-6">
        {/* Certificate Card */}
        <Card className="border-2 border-[#3CB371] bg-white shadow-xl overflow-hidden">
          {/* Header Banner */}
          <div className="bg-[#2B3A67] px-6 py-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Anchor className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="font-black text-lg font-['Outfit']">TRUSTED BOATER</h2>
                  <p className="text-sm text-white/80">Safety Compliance Certificate</p>
                </div>
              </div>
              {certificate.compliance.is_verified && (
                <div className="verified-badge bg-[#3CB371] px-3 py-2 rounded-lg">
                  <ShieldCheck className="w-6 h-6" />
                </div>
              )}
            </div>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Certificate ID */}
            <div className="text-center">
              <p className="text-xs font-bold text-[#4A5568] uppercase tracking-wide">Certificate ID</p>
              <p className="text-xl font-black text-[#2B3A67] font-mono">{certificate.certificate_id}</p>
            </div>

            {/* Compliance Score */}
            <div className="text-center p-6 bg-[#E8F6ED] rounded-2xl">
              <p className={`text-6xl font-black font-['Outfit'] ${
                certificate.compliance.score === 100 ? 'text-[#3CB371]' : 'text-[#E5A000]'
              }`}>
                {certificate.compliance.score}%
              </p>
              <p className="text-[#2A8A53] font-bold mt-2">
                {certificate.compliance.is_compliant ? 'COMPLIANT' : 'NOT COMPLIANT'}
              </p>
              {certificate.compliance.is_verified && (
                <div className="flex items-center justify-center gap-2 mt-3 text-[#2A8A53]">
                  <ShieldCheck className="w-5 h-5" />
                  <span className="font-bold text-sm">Verified Pre-Departure Check</span>
                </div>
              )}
            </div>

            {/* Boater & Vessel Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-[#F0F4F8] rounded-xl">
                <p className="text-xs font-bold text-[#4A5568] uppercase tracking-wide mb-1">Boater</p>
                <p className="font-bold text-[#2B3A67]">{certificate.user.name}</p>
              </div>
              <div className="p-4 bg-[#F0F4F8] rounded-xl">
                <p className="text-xs font-bold text-[#4A5568] uppercase tracking-wide mb-1">Vessel</p>
                <p className="font-bold text-[#2B3A67]">{certificate.vessel.name}</p>
              </div>
            </div>

            {/* Vessel Details */}
            <div className="flex items-center justify-center gap-6 text-sm text-[#4A5568]">
              <div className="flex items-center gap-1">
                <Ship className="w-4 h-4" />
                <span>Class {certificate.vessel.class}</span>
              </div>
              <div className="flex items-center gap-1">
                <Anchor className="w-4 h-4" />
                <span>{certificate.vessel.length}ft</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="capitalize">{certificate.vessel.engine_type}</span>
              </div>
            </div>

            {/* Trip Details */}
            <div className="flex items-center justify-center gap-6 text-sm text-[#4A5568]">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span className="capitalize">{certificate.trip_details.location}</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="capitalize">{certificate.trip_details.time_of_day}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{certificate.trip_details.passenger_count}</span>
              </div>
            </div>

            {/* Verified Items */}
            <div>
              <p className="text-xs font-bold text-[#4A5568] uppercase tracking-wide mb-3">
                Verified Safety Items ({certificate.compliance.required_items_verified}/{certificate.compliance.required_items_total})
              </p>
              <div className="space-y-2">
                {certificate.verified_items.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-[#3CB371]" />
                    <span className="text-[#2B3A67]">{item.name}</span>
                    {item.has_photo && (
                      <span className="text-xs bg-[#E8F6ED] text-[#2A8A53] px-2 py-0.5 rounded">📷</span>
                    )}
                    {item.count && (
                      <span className="text-xs text-[#4A5568]">×{item.count}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Timestamps */}
            <div className="pt-4 border-t border-[#E2E8F0] text-xs text-[#4A5568] space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Checked: {formatDate(certificate.timestamps.checklist_created)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Generated: {formatDate(certificate.timestamps.certificate_generated)}</span>
              </div>
            </div>

            {/* Verification Code & QR */}
            <div className="text-center pt-4 border-t border-[#E2E8F0]">
              <p className="text-xs text-[#4A5568] mb-1">Verification Code</p>
              <p className="font-mono font-bold text-[#2B3A67]">{certificate.verification_code}</p>
            </div>

            {/* QR Code */}
            <div className="flex flex-col items-center pt-2 pb-2" data-testid="certificate-qr-code">
              <div className="bg-white p-3 rounded-xl border border-[#E2E8F0]">
                <QRCode
                  value={shareUrl || verificationUrl}
                  size={140}
                  level="H"
                  fgColor="#2B3A67"
                />
              </div>
              <p className="text-[10px] text-[#4A5568] mt-2 text-center">
                Scan to verify this certificate
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Share URL Display */}
        {shareUrl && (
          <Card className="border-2 border-[#E2E8F0]">
            <CardContent className="p-4">
              <p className="text-sm font-bold text-[#2B3A67] mb-2">Share Link (expires in 24 hours)</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-[#F0F4F8] rounded-lg text-sm font-mono text-[#4A5568]"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="border-[#E2E8F0]"
                >
                  <Copy className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => window.open(shareUrl, '_blank')}
                  className="border-[#E2E8F0]"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E2E8F0] px-6 py-4 print:hidden">
        <div className="max-w-lg mx-auto flex gap-3">
          <Button
            data-testid="download-certificate-btn"
            onClick={handleDownloadPDF}
            variant="outline"
            className="flex-1 h-14 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-base rounded-xl"
          >
            <Download className="w-5 h-5 mr-2" />
            PDF
          </Button>
          <Button
            data-testid="email-certificate-btn"
            onClick={handleEmailCertificate}
            variant="outline"
            className="flex-1 h-14 border-2 border-[#2B3A67] text-[#2B3A67] font-bold text-base rounded-xl"
          >
            <Mail className="w-5 h-5 mr-2" />
            Email
          </Button>
          <Button
            data-testid="share-certificate-btn"
            onClick={handleShare}
            disabled={sharing}
            className="flex-1 h-14 bg-[#3CB371] hover:bg-[#2A8A53] text-white font-bold text-base rounded-xl"
          >
            <Share2 className="w-5 h-5 mr-2" />
            {sharing ? '...' : 'Share'}
          </Button>
        </div>
      </div>
    </div>
  );
}
